import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, Typography } from '@tempmee/react';
import { OutlineRefreshIcon } from '@tempmee/react-icons';
import { ListPageHeader } from '../../../commonComponents/ListPageHeader';
import { Spinner } from '../../../commonComponents/Spinner';
import { TabViewContainer } from '../../../commonComponents/TabViewContainer';
import { Table } from '../../../commonComponents/Table/Table';
import { getAdminRamUser } from '../../../../actions/userActions';
import { fetchRequestedEducations } from '../../../../actions/licensesAction';
import { Filters } from './components/Filters';
import { useJobList } from '../../../../domain/Jobs/useCases';
import { JobType } from '../../../../domain/Jobs/enums/JobType';
import { jobListColumn, jobListConditionalRowStyles } from '../../../commonComponents/jobs-column';
import { createLoadingSelector } from '../../../../apis/selectors';

const getJobUrl = (job) => `/job/${job.id}`;
const getUserUrl = (userId, userType) => `/user/${userType.toLowerCase()}/${userId}`;

const loadingSelector = createLoadingSelector(['GET_ADMIN_RAM', 'GET_DSO_NAMES']);

export const OnsiteInterviewList = () => {
  const dispatch = useDispatch();

  const isInitialLoading = useSelector((state) => loadingSelector(state));

  useEffect(() => {
    dispatch(getAdminRamUser());
    dispatch(fetchRequestedEducations());
  }, [dispatch]);

  const {
    data,
    isLoading,
    isFetching,
    filters,
    total,
    totalFiltered,
    totalPages,
    handleFilterChanged,
    handlePageChanged,
    handleSortChanged,
    reloadJobs,
  } = useJobList({ jobType: JobType.INTERVIEW });
  const getColumn = () =>
    jobListColumn({
      getUserUrl,
      getJobUrl,
      showCounterExpiration: ['pending', 'counter', 'pending-counter'].includes(filters?.jobStatus),
      cancellationTime: true,
      isOnsiteInterview: true,
    });

  if (isInitialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        overflowY: 'auto',
        flexDirection: 'column',
        position: 'relative',
        alignItems: 'center',
      }}
    >
      <TabViewContainer style={{ marginInline: 'auto', width: '95%', marginTop: 0 }}>
        <ListPageHeader
          title="Interviews"
          isLoading={isFetching}
          actions={(
            <IconButton
              aria-label="Reload interviews"
              appearance="default"
              Icon={<OutlineRefreshIcon />}
              onClick={reloadJobs}
            />
          )}
          meta={(
            <>
              <Typography size="body_large" bold>
                Total | {total !== undefined ? Intl.NumberFormat().format(total) : ''}
              </Typography>
              <Typography size="body_large" bold>
                Total {filters.jobStatus || 'all'} |{' '}
                {totalFiltered !== undefined ? Intl.NumberFormat().format(totalFiltered) : ''}
              </Typography>
            </>
          )}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Filters filters={filters} handleFilterChanged={handleFilterChanged} />
        </div>
        <TabViewContainer style={{ margin: 0, padding: 20 }}>
          <Table
            initialPage={filters.page}
            columns={getColumn()}
            data={data}
            noDataComponent="There are no Interviews"
            isLoading={isLoading}
            totalPages={totalPages}
            showTotal={false}
            onSort={handleSortChanged}
            paginationPerPage={filters.limit}
            paginationTotalRows={totalFiltered}
            onPageChange={handlePageChanged}
            conditionalRowStyles={jobListConditionalRowStyles}
            forceLoading={isLoading}
          />
        </TabViewContainer>
      </TabViewContainer>
    </div>
  );
};
